import React, { useState } from 'react';
import { useGetResources, useDeletePlanningItem } from '../../hooks/usePlanning';
import { useToast } from '../common/ToastProvider';
import { useNavigate } from 'react-router-dom';

function TaskDetailsPanel({ task, onClose, onUpdate }) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    startDate: task.start ? new Date(task.start).toISOString().split('T')[0] : '',
    endDate: task.end ? new Date(task.end).toISOString().split('T')[0] : '',
    assignedUserId: task.assignedUser?.id || '',
    priority: task.priority || 'Medium',
    status: task.status || 'Planned',
  });

  const { data: resources = [] } = useGetResources();
  const deletePlanningItem = useDeletePlanningItem();

  const handleChange = (field, value) => {
    setEditData({ ...editData, [field]: value });
  };

  const handleSave = () => {
    // Validate dates
    if (editData.startDate && editData.endDate && new Date(editData.endDate) < new Date(editData.startDate)) {
      showToast('End date must be after start date', 'error');
      return;
    }

    const updates = {
      ...editData,
      assignedUserId: editData.assignedUserId ? parseInt(editData.assignedUserId) : null,
    };

    onUpdate(updates);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (task.itemType !== 'planning') {
      showToast('Cannot delete company tasks from here', 'error');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this planning item?')) {
      return;
    }

    try {
      await deletePlanningItem.mutateAsync(task.itemId);
      showToast('Planning item deleted successfully', 'success');
      onClose();
    } catch (error) {
      console.error('Error deleting planning item:', error);
      showToast('Failed to delete planning item', 'error');
    }
  };

  const goToCompany = () => {
    if (task.companyId) {
      navigate(`/company/${task.companyId}`);
    }
  };

  const goToBranch = () => {
    if (task.branchId) {
      navigate(`/branch/${task.branchId}`);
    }
  };

  const priorityColors = {
    Low: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    Medium: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    High: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    Critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  };

  const statusColors = {
    'Not Started': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    'Planned': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    'In Progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    'On Hold': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    'Completed': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    'Cancelled': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <span>📋</span>
          <span>Task Details</span>
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Task Type Badge */}
        <div className="flex items-center gap-2">
          <span
            className="px-2 py-1 text-xs font-medium rounded-full"
            style={{
              backgroundColor: task.color + '20',
              color: task.color,
              border: `1px solid ${task.color}`,
            }}
          >
            {task.itemType === 'planning' ? task.type : 'Company Task'}
          </span>
          {task.branchName && (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
              Branch Task
            </span>
          )}
        </div>

        {/* Title */}
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">{task.title}</h3>
          {task.description && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{task.description}</p>
          )}
        </div>

        {/* Company and Branch Links */}
        <div className="space-y-2">
          {task.companyName && (
            <button
              onClick={goToCompany}
              className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              <span>🏢</span>
              <span>{task.companyName}</span>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </button>
          )}
          {task.branchName && (
            <button
              onClick={goToBranch}
              className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 hover:underline"
            >
              <span>🏪</span>
              <span>{task.branchName}</span>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </button>
          )}
        </div>

        <hr className="border-gray-200 dark:border-gray-700" />

        {/* Edit Mode */}
        {isEditing ? (
          <div className="space-y-4">
            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={editData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={editData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            {/* Assigned User */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Assigned To
              </label>
              <select
                value={editData.assignedUserId}
                onChange={(e) => handleChange('assignedUserId', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">Not assigned</option>
                {resources.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.role})
                  </option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Priority
              </label>
              <select
                value={editData.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={editData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                {task.itemType === 'planning' ? (
                  <>
                    <option value="Planned">Planned</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </>
                ) : (
                  <>
                    <option value="Not Started">Not Started</option>
                    <option value="In Progress">In Progress</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Completed">Completed</option>
                  </>
                )}
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Save
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          /* View Mode */
          <div className="space-y-4">
            {/* Dates */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Timeline
              </label>
              <div className="text-sm text-gray-900 dark:text-white">
                <div className="flex items-center gap-2">
                  <span>📅</span>
                  <span>
                    {task.start ? new Date(task.start).toLocaleDateString() : 'Not set'} →{' '}
                    {task.end ? new Date(task.end).toLocaleDateString() : 'Not set'}
                  </span>
                </div>
              </div>
            </div>

            {/* Assigned Users */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Assigned To
              </label>
              {task.assignedUsers && task.assignedUsers.length > 0 ? (
                <div className="space-y-2">
                  {task.assignedUsers.map((user) => (
                    <div key={user.id} className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {user.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {user.role}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : task.assignedUser ? (
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                    {task.assignedUser.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {task.assignedUser.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {task.assignedUser.role}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400">Not assigned</div>
              )}
            </div>

            {/* Priority and Status */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Priority
                </label>
                <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${priorityColors[task.priority] || priorityColors.Medium}`}>
                  {task.priority || 'Medium'}
                </span>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Status
                </label>
                <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${statusColors[task.status] || statusColors.Planned}`}>
                  {task.status}
                </span>
              </div>
            </div>

            {/* Completed By (if task is from CompanyTask) */}
            {task.completedBy && (
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Completed By
                </label>
                <div className="text-sm text-gray-900 dark:text-white">
                  {task.completedBy.name}
                </div>
              </div>
            )}

            {/* Action Button */}
            <button
              onClick={() => setIsEditing(true)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span>Edit Task</span>
            </button>
          </div>
        )}
      </div>

      {/* Footer - Delete Button (only for planning items) */}
      {task.itemType === 'planning' && !isEditing && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleDelete}
            disabled={deletePlanningItem.isPending}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {deletePlanningItem.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Deleting...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span>Delete Planning Item</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default TaskDetailsPanel;

